import Rollbar from 'rollbar';
import config from '../../rollbar.config';

/**
 * Exported so code outside the Vue error handler can report through the same client —
 * see `core/services/error-reporting.ts`. Rollbar no-ops when `config.enabled` is false,
 * so importing this is safe in environments without an access token.
 */
export const rollbar = new Rollbar(config);

/**
 * Vue's `errorHandler` receives whatever was thrown, which is not always an `Error`.
 * Rejected API payloads and bare strings give Rollbar nothing to derive a title or
 * trace from, so it reports them all as "Item sent with null or missing arguments."
 * — collapsing unrelated failures into a single, undiagnosable item.
 *
 * Wrapping them in a real `Error` restores per-failure grouping. Titles are kept
 * locale-independent on purpose: the API returns translated `message` values, and
 * interpolating those would split one bug into a group per language. The untouched
 * original is always attached as custom data.
 */
export function toReportableError(thrown: unknown): Error {
    if (thrown instanceof Error) return thrown;

    if (typeof thrown === 'string') return new Error(thrown);

    if (thrown && typeof thrown === 'object') {
        const { path, method, code, message } = thrown as Record<string, unknown>;
        const status = toHttpStatus(code);

        // The api client rethrows the parsed response body verbatim (`core/api/client.ts`),
        // which carries a string `path` and an HTTP `code`. Both checks earn their keep: a
        // truthy `path` + `code` pair alone also matches Node-style errors (`code: 'ENOENT'`)
        // and Zod issues (`path: ['email'], code: 'invalid_type'`), neither of which is an
        // HTTP failure and both of which would get a title claiming otherwise.
        if (typeof path === 'string' && status !== undefined) {
            return new Error(`${typeof method === 'string' ? method : 'REQUEST'} ${path} failed with ${status}`);
        }

        if (typeof message === 'string' && message) return new Error(message);
    }

    return new Error(`Non-Error value thrown: ${describeValue(thrown)}`);
}

/** Numeric strings are accepted so an endpoint returning `"401"` still groups by status. */
function toHttpStatus(value: unknown): number | undefined {
    if (typeof value !== 'number' && (typeof value !== 'string' || value.trim() === '')) return undefined;

    const status = Number(value);

    return Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined;
}

function describeValue(value: unknown): string {
    try {
        return JSON.stringify(value) ?? String(value);
    } catch {
        // Circular or otherwise unserialisable.
        return Object.prototype.toString.call(value);
    }
}

export default {
    install(app) {
        if (config.enabled === false) return;
        app.config.errorHandler = (error, _vm, info) => {
            const reportable = toReportableError(error);

            // `original` preserves the full payload — including any translated message —
            // since the reported title deliberately leaves it out.
            rollbar.error(reportable, { info, original: error });
        };
        app.provide('rollbar', rollbar);
    },
};
